import {
    Checkbox,
    makeStyles,
    Menu,
    MenuButton,
    MenuItem,
    MenuList,
    MenuPopover,
    MenuTrigger,
    mergeClasses,
    Tooltip,
    useAnnounce,
} from '@fluentui/react-components';
import { useBoolean } from '@fluentui/react-hooks';
import { Delete20Regular, Edit20Regular, MoreVertical20Filled } from '@fluentui/react-icons';
import { withAITracking } from '@microsoft/applicationinsights-react-js';
import { DataManager } from '@syncfusion/ej2-data';
import {
    ColumnDirective,
    ColumnModel,
    ColumnsDirective,
    ContextMenu,
    Edit,
    GridActionEventArgs,
    GridComponent,
    Inject,
    KeyboardEventArgs,
    PageSettingsModel,
    QueryCellInfoEventArgs,
    Resize,
    RowDataBoundEventArgs,
    SearchSettingsModel,
    SelectionSettingsModel,
    Sort,
    SortDirection,
    SortEventArgs,
    SortSettingsModel,
    ValueType,
    VirtualScroll,
} from '@syncfusion/ej2-react-grids';
import { useQueryClient } from '@tanstack/react-query';
import { RefObject, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useIsFeatureEnabled } from '../../../api/features/features';
import {
    useGetAllFolders,
    useGetLatestExecutionInformation,
    useGetTaxReturnById,
    useGetTaxReturns,
    useGetTaxReturnsNameList,
    useSetTaxReturnStatus,
    useUpdateTaxReturns,
} from '../../../api/report/report';
import { applicationInsightsReactPlugin } from '../../../ApplicationInsightsService';
import { GridStateContextProvider } from '../../../contexts/gridStateContext';
import { ReturnDetailsContextProvider } from '../../../contexts/returnDetails/returnDetailsContext';
import { TaxReturnCalculationProvider } from '../../../contexts/taxReturnCalculationContext';
import { useUserSettings } from '../../../contexts/useUserSettings';
import { ISelectedTaxReturnsAndFilters } from '../../../data-types/exportReports';
import { IFilterItem } from '../../../data-types/filterItem';
import { IExecutionStatusStorage } from '../../../data-types/IExecutionStatusStorage';
import { useLegacyReportsTableFiltering } from '../../../hooks/reportsTable/useLegacyReportsTableFiltering';
import { useFeedbackMessages } from '../../../hooks/useFeedbackMessages';
import { useFunctionalAccess } from '../../../hooks/useFunctionalAccess';
import { useHighlightSearchedValues } from '../../../hooks/useHighlightSearchedValues';
import { usePerformanceMetricsForTable } from '../../../hooks/usePerformanceMetricsForTable';
import { usePersistFocusInTable } from '../../../hooks/usePersistFocusInTable';
import { useSearchTable } from '../../../hooks/useSearchTable';
import { useTaxReturnCalculationState } from '../../../hooks/useTaxReturnCalculationState';
import {
    CalculationExecutionStatus,
    CalculationExecutionStatusCode,
    Report,
    ReportListRequest,
    ReportStatus,
    ReturnFolderDto,
    TaxReturnSaveRecordsRequest,
} from '../../../model';
import { REDIRECTED_EVENT_NAME } from '../../../telemetry/events';
import { useEventTracker } from '../../../telemetry/hooks/useEventTracker';
import { useUtmTracking } from '../../../telemetry/hooks/useUtmTracking';
import { cometDarkTheme, customPalette } from '../../../theme';
import { isResponseArrayValid } from '../../../utils/ApiClientUtils';
import { getSortComparer } from '../../../utils/ColumnUtils';
import {
    getLegacyCasesFilterItem,
    getLegacyEntitiesFilterItem,
    getLegacyFilterOptions,
    getSelectedTaxReturnsAndFilters,
} from '../../../utils/FilterUtils';
import {
    adaptReportToShowableTaxReturnItemRecord,
    getLegacyReturnRequest,
    getReportPropertyValueByFieldName,
} from '../../../utils/ReportUtils';
import { getLegacyTableKeyWithColumnsFilters, scrollToTop } from '../../../utils/TableUtils';
import AddReturnPanel, { ShowableTaxReturnItemRecord } from '../../add-return/AddReturnPanel';
import ConfirmReturnDeletionModal from '../../common/ConfirmReturnDeletionModal';
import { DefaultTooltipHostV9 } from '../../common/DefaultTooltipHostV9';
import LoadingSpinner from '../../common/LoadingSpinner';
import { ReportStatusButton } from '../../common/ReportStatusButton';
import EditReturnModal from '../../edit-return/EditReturnModal';
import { AlwaysDisplayClassName, DisplayOnActiveRowClassName, StyledTable } from '../StyledTable.legacy';
import FilterBar from './FilterBar.legacy';
import LastExecutedReportLink from './LastExecutedReportLink';
import ReportLink from './ReportLink';

const sortingOptions: SortSettingsModel = {
    columns: [{ field: 'name', direction: 'Ascending' }],
};
const pageSettings: PageSettingsModel = { pageSize: 50 };
const numberOfColumns = 10;
const reportNameColumnIndex = '2';

/** Syncfusion `customAttributes.class` marker (single CSS token). */
const REPORT_NAME_CELL_FOCUS_CLASS = 'report-name-cell-focus';

const useStyles = makeStyles({
    disabledCheckboxIndicator: {},
    wrapper: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
    },
    wrapperWithMessage: {
        height: 'calc(100vh - 152px)',
    },
    wrapperWithoutMessage: {
        height: 'calc(100vh - 120px)',
    },
    filterBar: {
        position: 'sticky',
        top: 0,
        zIndex: 2,
    },
    scrollablePane: {
        position: 'relative',
        backgroundColor: customPalette.themeLighterAlt,
        borderTopWidth: 0,
        borderRightWidth: '1px',
        borderBottomWidth: '1px',
        borderLeftWidth: '1px',
        borderTopStyle: 'none',
        borderRightStyle: 'solid',
        borderBottomStyle: 'solid',
        borderLeftStyle: 'solid',
        borderRightColor: customPalette.greyD1D3D4,
        borderBottomColor: customPalette.greyD1D3D4,
        borderLeftColor: customPalette.greyD1D3D4,
        boxShadow: 'none',
        overflow: 'auto',
        marginTop: 0,
        marginRight: '15px',
        marginBottom: 0,
        marginLeft: '15px',
        [`& .${REPORT_NAME_CELL_FOCUS_CLASS}:focus`]: {
            textDecoration: 'underline',
            '& span': {
                color: customPalette.themePrimary,
            },
        },
    },
    loadingSpinnerContainer: {
        height: '100%',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    cellContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        ':focus-within [data-fui-focus-visible]': {
            textDecoration: 'underline',
            '& span': {
                color: customPalette.themePrimary,
            },
        },
    },
    reportNameCell: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        position: 'relative',
        width: '100%',
    },
    optionsButton: {
        border: 'none',
        display: 'none',
        height: '32px',
        '&[data-fui-focus-visible]': {
            border: `1px solid ${customPalette.themePrimary}`,
        },
    },
    disabledItem: {
        color: cometDarkTheme.colorNeutralStrokeDisabled,
    },
    warningIcon: {
        fontSize: '16px',
        marginLeft: '2px',
        border: 'none',
        backgroundColor: 'inherit',
        padding: '0',
    },
    buttonFocused: {
        ':focus ~ [data-fui-focus-visible]': {
            textDecoration: 'none',
        },
    },
});

export const EMPTY_CONTEXT_KEY_VALUE = 0;

const ReportsTable = () => {
    const styles = useStyles();
    const { trackScrollStarted, trackScrollFinished, trackRenderFinished } = usePerformanceMetricsForTable('List of returns');
    const { t } = useTranslation();
    const { functionalAccess } = useFunctionalAccess();
    const { activeMessage } = useFeedbackMessages();
    const queryClient = useQueryClient();

    const { entityFilterName, caseFilterName, jurisdictionFilterName, periodFilterName, folderFilterName, columnFilters, clearAllFilters } =
        useLegacyReportsTableFiltering();

    const [folders, setFolders] = useState<IFilterItem[]>([]);
    const [disableActionButtons, setDisableActionButtons] = useState<boolean>(true);
    const { focusOnSelectedCell, setSelectedCellIndex, selectedCellIndex } = usePersistFocusInTable();
    const sortingDirection = useRef<SortDirection | undefined>('Ascending');
    const [hasLatestCalculationStatus, { setTrue: setHasLatestCalculationStatusTrue }] = useBoolean(false);
    const { registerGlobalDependency, fetchAllCalculationExecutionStatuses, removeTaxReturnCalculationReport } =
        useTaxReturnCalculationState();
    const { mutateAsync: getLatestExecutionInformation } = useGetLatestExecutionInformation();
    const { displayFeedbackMessage } = useFeedbackMessages();
    const { mutateAsync: updateTaxReturns } = useUpdateTaxReturns();
    const [defaultSortingEnabled, { setFalse: disableDefaultSorting }] = useBoolean(true);
    const { removeReturn } = useUserSettings();

    const [isAddReturnPanelOpen, { setTrue: openAddReturnPanel, setFalse: closeAddReturnPanel }] = useBoolean(false);
    const [isEditReturnModalOpen, { setTrue: openEditReturnModal, setFalse: closeEditReturnModal }] = useBoolean(false);
    const [isDeleteReturnModalOpen, { setTrue: openDeleteReturnModal, setFalse: closeDeleteReturnModal }] = useBoolean(false);

    const [returnToDelete, setReturnToDelete] = useState<number | undefined>(undefined);
    const { announce } = useAnnounce();

    const [selectedReturn, setSelectedReturn] = useState<ShowableTaxReturnItemRecord | undefined>(undefined);
    const { data: allReturnsNames, queryKey: getTaxReturnsNameListQueryKey } = useGetTaxReturnsNameList({
        reportId: selectedReturn?.taxReturnItemKey,
    });
    const { refetch: refetchReport } = useGetTaxReturnById<Report>(selectedReturn?.taxReturnItemKey ?? 0, { query: { enabled: false } });

    const { data: isCreateNewReturnEnabled } = useIsFeatureEnabled({
        featureFlagName: 'CreateNewReturnEnabled',
    });
    const { data: isEditCalculationEnhancementsEnabled } = useIsFeatureEnabled({
        featureFlagName: 'EditCalculationEnhancementsEnabled',
    });
    const [recentlyCreatedTaxReturnItemKeys, setRecentlyCreatedTaxReturnItemKeys] = useState<number[] | undefined>(undefined);
    document.title = t('review');

    const name = 'name';
    const userFullName = 'userFullName';

    const getExecutedTemplate = (data: Report) => {
        return <LastExecutedReportLink {...data} />;
    };

    const gridRef = useRef<GridComponent>(null);
    const headerCheckboxClickCleanupRef = useRef<(() => void) | null>(null);
    const handleHeaderCheckboxToggleRef = useRef<(event: Event) => void>(() => undefined);
    const selectAllActiveRef = useRef(false);
    useEffect(
        () => () => {
            headerCheckboxClickCleanupRef.current?.();
            headerCheckboxClickCleanupRef.current = null;
        },
        []
    );

    const getTaxReturnsRequest = (): ReportListRequest => {
        const selectedFolderKey = columnFilters[folderFilterName]?.length ? columnFilters[folderFilterName][0]?.key : '';
        return getLegacyReturnRequest(
            columnFilters,
            parseInt(selectedFolderKey),
            entityFilterName,
            jurisdictionFilterName,
            periodFilterName,
            caseFilterName
        );
    };

    const updateTaxReturnRowDataInPlace = (taxReturnItemKey: number, updatedInformation: any, refresh: boolean = false) => {
        const report = filteredReportsResponse?.reports?.find((report) => report.id === taxReturnItemKey);
        if (report) {
            dataManager.update('id', { ...updatedInformation, id: taxReturnItemKey });
        }

        if (refresh) {
            gridRef.current?.refresh();
        } else {
            gridRef.current?.setRowData(taxReturnItemKey, { ...report, ...updatedInformation, id: taxReturnItemKey });
        }

        focusOnSelectedCell(false);
    };

    const deleteTaxReturnRowDataInPlace = (taxReturnItemKey: number) => {
        gridRef.current?.deleteRecord('id', taxReturnItemKey);
        removeTaxReturnCalculationReport(taxReturnItemKey);
        removeReturn(taxReturnItemKey);
    };

    const updateReturnLastExecutedDateInList = (
        taxReturnItemKey: number,
        lastExecuted: string,
        lastExecutedBy?: string,
        refresh: boolean = false
    ) => {
        const valuesToUpdate: any = { executed: lastExecuted };

        if (lastExecutedBy) {
            valuesToUpdate[userFullName] = lastExecutedBy;
        }

        updateTaxReturnRowDataInPlace(taxReturnItemKey, valuesToUpdate, refresh);
    };

    const { mutateAsync, isLoading: isLoadingSetStatus } = useSetTaxReturnStatus({
        mutation: {
            onSuccess: async (data: any, { reportId, params }) => {
                if (data) {
                    updateTaxReturnRowDataInPlace(reportId, { status: params?.reportStatus }, false);
                }
            },
        },
    });

    useEffect(() => {
        if (isLoadingSetStatus) {
            gridRef.current?.showSpinner();
        } else {
            gridRef.current?.hideSpinner();
        }
    }, [isLoadingSetStatus]);

    const { data: folderItems, refetch: updateFolderList } = useGetAllFolders();
    const { getUtmParams, cleanUtmParamsFromUrl } = useUtmTracking();
    const { trackEvent, isReady } = useEventTracker();
    const { data: filteredReportsResponse, isError, isLoading, mutateAsync: getTaxReturns } = useGetTaxReturns();

    const dataManager = useMemo(() => new DataManager(filteredReportsResponse?.reports ?? []), [filteredReportsResponse]);
    const contentLength = filteredReportsResponse?.reports?.length ?? 0;

    const memoizedFilters = useMemo(() => getTaxReturnsRequest(), [columnFilters]);

    const displayCalculationStatusInTable = (taxReturnItemKey: string, calculationExecutionStatus: CalculationExecutionStatus) => {
        if (calculationExecutionStatus.statusCode === CalculationExecutionStatusCode.Executing) {
            updateReturnLastExecutedDateInList(parseInt(taxReturnItemKey), t('calculating'), undefined);
        } else if (calculationExecutionStatus.statusCode === CalculationExecutionStatusCode.Canceled) {
            updateReturnLastExecutedDateInList(parseInt(taxReturnItemKey), t('canceled'), undefined);
        }
    };

    useEffect(() => {
        // For '/' root page, track the utm event if present.
        const { isValid, params } = getUtmParams();
        if (params && isValid && isReady) {
            trackEvent(REDIRECTED_EVENT_NAME, params);
            cleanUtmParamsFromUrl();
        }
    }, [getUtmParams, cleanUtmParamsFromUrl, trackEvent, isReady]);

    useEffect(() => {
        registerGlobalDependency('ReportsTableTrigger', (calculationStatus: IExecutionStatusStorage) => {
            if (calculationStatus.taxReturnItemKey && calculationStatus.statusCode !== CalculationExecutionStatusCode.Executing) {
                getLatestExecutionInformation({ reportId: calculationStatus.taxReturnItemKey }).then((res) => {
                    updateReturnLastExecutedDateInList(
                        calculationStatus.taxReturnItemKey || 0,
                        res?.executionDate!,
                        res?.userFullName!,
                        false
                    );
                });
            } else {
                displayCalculationStatusInTable(calculationStatus.taxReturnItemKey?.toString() || '', calculationStatus);
            }
        });

        fetchAllCalculationExecutionStatuses().then((statusMap: { [key: string]: CalculationExecutionStatus } | undefined) => {
            if (statusMap) {
                for (const [key, value] of Object.entries(statusMap)) {
                    displayCalculationStatusInTable(key, value);
                }
            }
            setHasLatestCalculationStatusTrue();
        });
    }, [filteredReportsResponse]);

    useEffect(() => {
        if (recentlyCreatedTaxReturnItemKeys) {
            getTaxReturns({ data: { priorityTaxReturnItemKeys: recentlyCreatedTaxReturnItemKeys } });
            setRecentlyCreatedTaxReturnItemKeys(undefined);
        } else {
            getTaxReturns({ data: memoizedFilters });
        }
    }, [memoizedFilters]);

    const { storeSearchValueToHighlight, highlightSearchValueOnRender, clearSearch, hasMatch } =
        useHighlightSearchedValues(getReportPropertyValueByFieldName);
    const { clearSearch: clearSearchBar } = useSearchTable();

    const entities = getLegacyEntitiesFilterItem(filteredReportsResponse?.availableFilterOptions?.entities ?? []);
    const entityGroups = getLegacyEntitiesFilterItem(filteredReportsResponse?.availableFilterOptions?.entityGroups ?? []);
    const cases = getLegacyCasesFilterItem(filteredReportsResponse?.availableFilterOptions?.cases ?? []);
    const jurisdictions = getLegacyFilterOptions(filteredReportsResponse?.availableFilterOptions?.jurisdictions);
    const periods = getLegacyFilterOptions(filteredReportsResponse?.availableFilterOptions?.periods);
    const tableKey = getLegacyTableKeyWithColumnsFilters('ReportsTable', columnFilters);

    const updateFolders = (items: ReturnFolderDto[]) => {
        const folders: IFilterItem[] = [];
        items.forEach((item) => {
            if (!folders.some((e) => e.key === item.folderKey?.toString())) {
                folders.push({
                    key: item.folderKey?.toString() ?? '',
                    label: item.folderName ? `${item.folderName} (${item.returnCount})` : '',
                    searchBy: [item.folderName ?? ''],
                });
            }
        });

        setFolders(folders);
    };

    useEffect(() => {
        if (isResponseArrayValid(folderItems)) {
            updateFolders(folderItems ?? []);
        }
    }, [folderItems]);

    const clickOnChildLink = (targetElement: HTMLElement) => {
        const isReportNameColumn = targetElement?.getAttribute('data-colindex') === reportNameColumnIndex;
        const isLinkElement = targetElement?.role === 'link';

        if (isReportNameColumn) {
            targetElement.querySelector('a')?.click();
            return;
        }

        if (isLinkElement) {
            targetElement.click();
            return;
        }
    };

    const gridKeyPressed = (args: KeyboardEventArgs) => {
        if (gridRef && gridRef.current) {
            gridRef.current.focusModule.content.target = undefined;
        }

        const targetElement = args.target as HTMLElement;

        if (args.code === 'Enter' || args.code === 'Space') {
            if (targetElement && !targetElement.classList.contains('e-headercell')) {
                args.cancel = true;
                clickOnChildLink(targetElement);
            }
        }

        if (args.code === 'ArrowRight') {
            let cellElement = targetElement;

            if (targetElement.tagName !== 'TD' && targetElement.tagName !== 'BUTTON') {
                cellElement = targetElement.closest('td') as HTMLElement;
            }

            if (cellElement) {
                const childButton = cellElement.querySelector('button');

                if (childButton) {
                    args.cancel = true;
                    args.preventDefault();
                    childButton.focus();
                }
            }
        }
    };

    const reportNameTemplateTruncated = (item: Report) => {
        const index = (item as any).index;

        const onEditClick = () => {
            const taxReturn = adaptReportToShowableTaxReturnItemRecord(item);

            setSelectedCellIndex({
                cellIndex: 2,
                rowIndex: index,
            });
            setSelectedReturn(taxReturn);
            openEditReturnModal();
        };

        const onDeleteClick = () => {
            setReturnToDelete(item.id);
            setSelectedCellIndex({
                cellIndex: 2,
                rowIndex: parseInt(index),
            });
            openDeleteReturnModal();
        };

        const makeMenuIconAlwaysVisible = (menuIconElement: HTMLElement) => {
            menuIconElement.classList.remove(DisplayOnActiveRowClassName);
            menuIconElement.classList.add(AlwaysDisplayClassName);
        };

        const makeMenuIconVisibleForActiveRows = (menuIconElement: HTMLElement) => {
            menuIconElement.classList.remove(AlwaysDisplayClassName);
            menuIconElement.classList.add(DisplayOnActiveRowClassName);
        };

        if (!functionalAccess?.viewTaxReturnsEnabled) {
            return (
                <DefaultTooltipHostV9
                    content={t('noFunctionalAccess').toString()}
                    alwaysDisplay
                    relationship='description'
                    appearance='inverted'
                    withArrow
                    positioning='after'
                    containerClassName={styles.disabledItem}
                >
                    <div className={styles.cellContainer}>
                        <label tabIndex={0} className={styles.disabledItem} aria-disabled aria-label={t('noFunctionalAccess').toString()}>
                            {item.name}
                        </label>
                        {isCreateNewReturnEnabled && (
                            <MoreVertical20Filled
                                aria-label={t('noFunctionalAccess').toString()}
                                style={{
                                    color: cometDarkTheme.colorNeutralStrokeDisabled,
                                }}
                            />
                        )}
                    </div>
                </DefaultTooltipHostV9>
            );
        }

        return (
            <div className={styles.cellContainer}>
                <DefaultTooltipHostV9
                    appearance='inverted'
                    withArrow
                    positioning='after'
                    content={item.name}
                    containerClassName={!item.canExecute ? styles.disabledItem : undefined}
                >
                    {!item.canExecute ? (
                        <label
                            aria-disabled
                            className={styles.disabledItem}
                            onPointerEnter={() => {
                                // Tooltip describes item.name; announce unavailable status separately.
                                announce(t('unavailable').toString(), { batchId: `reports-unavailable-${item.id}` });
                            }}
                        >
                            {item.name}
                        </label>
                    ) : (
                        <ReportLink report={item} tabIndex={-1} />
                    )}
                </DefaultTooltipHostV9>
                {isCreateNewReturnEnabled && (
                    <Menu
                        onOpenChange={(event, data) => {
                            const target =
                                event.target instanceof HTMLElement
                                    ? event.target.closest<HTMLElement>(`[data-testid="return-options-${index}"]`)
                                    : null;
                            if (!target) {
                                return;
                            }
                            if (data.open) {
                                makeMenuIconAlwaysVisible(target);
                            } else {
                                makeMenuIconVisibleForActiveRows(target);
                            }
                        }}
                    >
                        <MenuTrigger disableButtonEnhancement>
                            <MenuButton
                                appearance='subtle'
                                data-testid={`return-options-${index}`}
                                className={mergeClasses(styles.optionsButton, DisplayOnActiveRowClassName)}
                                menuIcon={<MoreVertical20Filled />}
                                aria-label={t('moreActionsForRow', { calcFileName: item.name }).toString()}
                            />
                        </MenuTrigger>
                        <MenuPopover>
                            <MenuList>
                                <MenuItem
                                    icon={<Edit20Regular />}
                                    disabled={!functionalAccess?.editReportsButtonEnabled || !item.canExecute}
                                    onClick={onEditClick}
                                >
                                    {t('editDetails').toString()}
                                </MenuItem>
                                {isEditCalculationEnhancementsEnabled && (
                                    <MenuItem
                                        icon={<Delete20Regular />}
                                        disabled={!functionalAccess?.deleteReportsButtonEnabled}
                                        onClick={onDeleteClick}
                                    >
                                        {t('deleteCalculation').toString()}
                                    </MenuItem>
                                )}
                            </MenuList>
                        </MenuPopover>
                    </Menu>
                )}
            </div>
        );
    };

    const reportHeaderTemplate = (item: ColumnModel) => {
        return (
            <DefaultTooltipHostV9 appearance='inverted' withArrow positioning='after' content={item.headerText}>
                <label>{item.headerText}</label>
            </DefaultTooltipHostV9>
        );
    };

    const reportStatusTemplate = (item: Report) => {
        return (
            <DefaultTooltipHostV9
                relationship='description'
                appearance='inverted'
                withArrow
                positioning='after'
                content={t(item.status ?? '')}
            >
                <ReportStatusButton
                    key={item.id}
                    status={item.status ?? ReportStatus.NotStarted}
                    onChange={(status) => {
                        handleStatusChange(item.id!, status);
                    }}
                    setSelectedCellIndex={setSelectedCellIndex}
                    isCell={true}
                    tabIndex={-1}
                    disabled={!item.canExecute || !functionalAccess?.viewTaxReturnsEnabled}
                />
            </DefaultTooltipHostV9>
        );
    };

    const getColumnTemplate = (fieldName: string) => {
        return (item: Report) => {
            const propertyKey = fieldName as keyof Report;
            const isDisabledText = !(item.canExecute && functionalAccess?.viewTaxReturnsEnabled);

            return (
                <DefaultTooltipHostV9
                    appearance='inverted'
                    withArrow
                    positioning='after'
                    content={item[propertyKey]?.toString()}
                    containerClassName={isDisabledText ? styles.disabledItem : undefined}
                >
                    <label tabIndex={-1} className={isDisabledText ? styles.disabledItem : undefined}>
                        {item[propertyKey]}
                    </label>
                </DefaultTooltipHostV9>
            );
        };
    };

    const handleStatusChange = async (reportId: number, key: ReportStatus) => {
        try {
            //todo: processing indicator
            await mutateAsync({ reportId: reportId, params: { reportStatus: key } });
            //todo: error indicator
        } catch (error: any) {
            if (error) {
                throw new Error(error?.request?.response);
            }
        }
    };

    const handleReportToExport = (): ISelectedTaxReturnsAndFilters => {
        const selectedRecords = (gridRef.current?.getSelectedRecords() ?? []) as Report[];
        return getSelectedTaxReturnsAndFilters(gridRef, getTaxReturnsRequest, selectedRecords);
    };

    if (isError) {
        return (
            <div>
                <label>{t('error')}</label>
            </div>
        );
    }

    const selectionSettings: SelectionSettingsModel = {
        checkboxOnly: true,
        persistSelection: true,
    };

    const queryCellInfo = (args: QueryCellInfoEventArgs) => {
        if (args.cell?.classList.contains('e-gridchkbox')) {
            args.cell?.querySelector('input')?.setAttribute('tabindex', '-1');
        }

        highlightSearchValueOnRender(args);
    };

    const handleActionComplete = (args: GridActionEventArgs) => {
        if (args.requestType === 'searching') {
            scrollToTop(gridRef.current);
        }
        if (args.requestType === 'sorting') {
            syncHeaderCheckboxToSelection();
        }
    };

    const handleActionBegin = (args: GridActionEventArgs) => {
        if (args.requestType === 'sorting') {
            sortingDirection.current = (args as SortEventArgs).direction ?? undefined;
        }

        storeSearchValueToHighlight(args);
    };

    const searchSetting: SearchSettingsModel = {
        fields: [name, entityFilterName, jurisdictionFilterName, caseFilterName, userFullName],
    };

    const sortComparer = (reference: ValueType, comparer: ValueType): number => {
        return getSortComparer(reference, comparer, sortingDirection.current?.toString() || '');
    };

    type ReportsSelectionModule = {
        persistSelectedData?: Report[];
        selectedRowState?: Record<string, boolean>;
        isHdrSelectAllClicked?: boolean;
        isPartialSelection?: boolean;
    };

    const getReportsSelectionModule = (): ReportsSelectionModule | undefined =>
        (gridRef.current as { selectionModule?: ReportsSelectionModule } | null)?.selectionModule;

    const isReportSelectable = (report: Report) => report.id != null && !!report.canExecute && !!functionalAccess?.viewTaxReturnsEnabled;

    const getSelectableReports = () => (filteredReportsResponse?.reports ?? []).filter(isReportSelectable);

    // Virtualization + persistSelection + even one isSelectable=false row (canExecute) turns on
    // Syncfusion isPartialSelection. After sort, setCheckAllState then compares persist count to the
    // 50-row viewport and forces e-stop; clicks always select and never uncheck.
    const syncHeaderCheckboxToSelection = () => {
        const grid = gridRef.current;
        if (!grid?.element) {
            return;
        }
        const selectableIds = getSelectableReports().map((report) => report.id as number);
        const selectedIds = new Set(
            ((grid.getSelectedRecords() ?? []) as Report[]).map((record) => record.id).filter((id): id is number => id != null)
        );
        const selectedInScope = selectableIds.filter((id) => selectedIds.has(id)).length;
        const nextState = selectAllActiveRef.current
            ? 'check'
            : selectableIds.length > 0 && selectedInScope === selectableIds.length
            ? 'check'
            : selectedInScope === 0
            ? 'uncheck'
            : 'stop';
        grid.element.querySelectorAll<HTMLInputElement>('.e-checkselectall').forEach((input) => {
            const frame = input.nextElementSibling;
            if (!(frame instanceof HTMLElement)) {
                return;
            }
            frame.classList.remove('e-check', 'e-stop', 'e-uncheck');
            if (nextState === 'check') {
                frame.classList.add('e-check');
                input.checked = true;
                input.indeterminate = false;
            } else if (nextState === 'uncheck') {
                frame.classList.add('e-uncheck');
                input.checked = false;
                input.indeterminate = false;
            } else {
                frame.classList.add('e-stop');
                input.checked = false;
                input.indeterminate = true;
            }
        });
        (grid as { checkAllRows?: string }).checkAllRows =
            nextState === 'check' ? 'Check' : nextState === 'uncheck' ? 'Uncheck' : 'Intermediate';
        const selection = getReportsSelectionModule();
        if (selection) {
            selection.isHdrSelectAllClicked = nextState === 'check';
        }
    };

    const applyPersistedSelection = (records: Report[]) => {
        const grid = gridRef.current;
        const selection = getReportsSelectionModule();
        if (!grid || !selection) {
            return;
        }
        selection.persistSelectedData = records.slice();
        selection.selectedRowState = Object.fromEntries(records.map((report) => [String(report.id), true]));
        selection.isHdrSelectAllClicked = records.length > 0;
        (grid as { checkAllRows?: string }).checkAllRows = records.length > 0 ? 'Check' : 'Uncheck';
        if (records.length === 0) {
            grid.clearSelection();
            return;
        }
        const viewIds = new Set(((grid.getCurrentViewRecords() ?? []) as Report[]).map((report) => report.id));
        const indexes = records
            .filter((report) => report.id != null && viewIds.has(report.id))
            .map((report) => grid.getRowIndexByPrimaryKey(report.id as number))
            .filter((index): index is number => typeof index === 'number' && index >= 0);
        if (indexes.length > 0) {
            grid.selectRows(indexes);
        }
    };

    const handleHeaderCheckboxToggle = (event: Event) => {
        const target = event.target;
        if (!(target instanceof Element)) {
            return;
        }
        const headerCheckboxCell = target.closest('th.e-headercell');
        if (!headerCheckboxCell?.querySelector('.e-headerchkcelldiv')) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        const selectedCount = selectAllActiveRef.current
            ? getSelectableReports().length
            : (gridRef.current?.getSelectedRecords() ?? []).length;
        const nextRecords = selectedCount > 0 ? [] : getSelectableReports();
        selectAllActiveRef.current = nextRecords.length > 0;
        applyPersistedSelection(nextRecords);
        handleDisableActionButtons(gridRef, setDisableActionButtons);
        syncHeaderCheckboxToSelection();
    };
    handleHeaderCheckboxToggleRef.current = handleHeaderCheckboxToggle;

    const restoreSelectAllIfNeeded = () => {
        if (!selectAllActiveRef.current) {
            return;
        }
        const selectable = getSelectableReports();
        const persistCount = getReportsSelectionModule()?.persistSelectedData?.length ?? 0;
        if (persistCount !== selectable.length) {
            applyPersistedSelection(selectable);
        }
    };

    const handleRowSelected = () => {
        if (selectAllActiveRef.current) {
            setDisableActionButtons(false);
            restoreSelectAllIfNeeded();
            syncHeaderCheckboxToSelection();
            return;
        }
        handleDisableActionButtons(gridRef, setDisableActionButtons);
        syncHeaderCheckboxToSelection();
    };

    const checkForOutdatedReports = () => {
        const executingReports = gridRef.current
            ?.getCurrentViewRecords()
            .filter((item: any) => item.executed === t('calculating').toString()) as Report[];

        executingReports?.forEach((executingReport) => {
            const outdatedReport = dataManager.dataSource.json?.find((item: Report) => item.id === executingReport.id) as Report;
            if (outdatedReport.id) {
                gridRef.current?.setRowData(outdatedReport.id, outdatedReport);
            }
        });
    };

    const getReportsTablePerformanceInfo = () => ({
        rowCount: contentLength,
        columnCount: numberOfColumns,
        visibleColumnCount: numberOfColumns,
    });

    const onDatabound = () => {
        // This is called after any updates to the table data
        checkForOutdatedReports();
        trackScrollFinished(getReportsTablePerformanceInfo());
        restoreSelectAllIfNeeded();
        syncHeaderCheckboxToSelection();

        if (selectedCellIndex) {
            // Timeout needed due to virtualization re-render
            setTimeout(() => {
                if (selectedCellIndex?.rowIndex !== undefined && gridRef.current) {
                    focusOnSelectedCell(false);
                    setSelectedCellIndex(undefined);
                }
            }, 100);
        }
    };

    const onCreated = () => {
        // This is the first time the table is created and rendered to the HTML DOM
        trackRenderFinished(getReportsTablePerformanceInfo());

        headerCheckboxClickCleanupRef.current?.();
        const gridElement = gridRef.current?.element;
        if (!gridElement) {
            return;
        }
        const onHeaderClick = (event: Event) => {
            const target = event.target;
            if (target instanceof Element && target.closest('.e-rowcell.e-gridchkbox') && !target.closest('th')) {
                selectAllActiveRef.current = false;
            }
            handleHeaderCheckboxToggleRef.current(event);
        };
        gridElement.addEventListener('click', onHeaderClick, true);
        headerCheckboxClickCleanupRef.current = () => {
            gridElement.removeEventListener('click', onHeaderClick, true);
        };
    };
    const onActionBegin = (args: GridActionEventArgs) => {
        if (args.requestType === 'virtualscroll') {
            trackScrollStarted();
        }
        handleActionBegin(args);
    };

    const closeAddReturnPanelAndRefresh = (createdTaxReturnItemKeys: number[]) => {
        clearSearch();
        clearSearchBar(gridRef);

        if (isEditCalculationEnhancementsEnabled) {
            queryClient.invalidateQueries(getTaxReturnsNameListQueryKey);
            disableDefaultSorting();
            gridRef.current?.sortModule.clearSorting();
            setRecentlyCreatedTaxReturnItemKeys(createdTaxReturnItemKeys);
            clearAllFilters();
        } else {
            getTaxReturns({ data: memoizedFilters });
        }

        closeAddReturnPanel();
    };

    const updateRowInPlaceAfterEditing = async (item: ShowableTaxReturnItemRecord) => {
        const shouldClearExecutedData = item.contextKey === EMPTY_CONTEXT_KEY_VALUE;

        const updatedReturn = await refetchReport();
        if (updatedReturn.data) {
            const taxReturn = updatedReturn.data;
            if (shouldClearExecutedData) {
                removeTaxReturnCalculationReport(taxReturn.id!);
                removeReturn(taxReturn.id!);
                taxReturn.executed = undefined;
                taxReturn.userFullName = undefined;
            }
            updateTaxReturnRowDataInPlace(taxReturn?.id || 0, taxReturn);
        }
    };

    const onSubmitEditReturn = async (editedData: ShowableTaxReturnItemRecord | undefined) => {
        if (editedData) {
            const request: TaxReturnSaveRecordsRequest = { taxReturnItemRecords: [editedData] };

            const saveResponse = await updateTaxReturns({ data: request });
            if (!saveResponse.hasErrors) {
                displayFeedbackMessage({ type: 'success', message: t('calcEdited') });
                await updateRowInPlaceAfterEditing(editedData);
            } else {
                displayFeedbackMessage({ type: 'error', message: t('calcNotEdited') });
            }

            setSelectedReturn(undefined);
        }
    };

    const renderDisabledCheckbox = () => <Checkbox disabled indicator={{ className: styles.disabledCheckboxIndicator }} />;

    const getWarningIconElement = (item: Report) => {
        const executeWarningMessage = item.executeWarningMessage || '';

        if (item && !item.canExecute) {
            return (
                <Tooltip content={executeWarningMessage} relationship='description' appearance='inverted' withArrow positioning='after'>
                    <button
                        tabIndex={-1}
                        aria-label={executeWarningMessage}
                        aria-disabled
                        className={mergeClasses('e-icons', 'e-warning', styles.warningIcon)}
                        style={{
                            color: customPalette.goldenrod,
                        }}
                    />
                </Tooltip>
            );
        }

        if (!functionalAccess?.viewTaxReturnsEnabled) {
            return renderDisabledCheckbox();
        }

        return;
    };

    const handleRowDataBound = (args: RowDataBoundEventArgs) => {
        const reportData = args.data as Report;
        if (args.data && (!reportData.canExecute || !functionalAccess?.viewTaxReturnsEnabled) && args.row) {
            args.isSelectable = false;

            const checkBoxElement = args.row.querySelector('.e-checkbox-wrapper') as HTMLElement;
            if (checkBoxElement) {
                checkBoxElement.style.display = 'none';
                const checkboxelement = checkBoxElement.firstChild as HTMLElement;
                checkboxelement.remove();
            }
        }
    };

    return (
        <GridStateContextProvider grid={gridRef}>
            <ReturnDetailsContextProvider>
                <TaxReturnCalculationProvider>
                    <div className={mergeClasses(styles.wrapper, activeMessage ? styles.wrapperWithMessage : styles.wrapperWithoutMessage)}>
                        <div className={styles.filterBar}>
                            <FilterBar
                                updateFolderList={updateFolderList}
                                disableActionButtons={disableActionButtons}
                                isFiltered={filteredReportsResponse?.isFiltered || false}
                                clearHighlightedElements={clearSearch}
                                periods={periods}
                                entities={entities}
                                entityGroups={entityGroups}
                                cases={cases}
                                jurisdictions={jurisdictions}
                                folders={folders}
                                disabled={isLoading}
                                getReportsToExport={handleReportToExport}
                                openAddReturnSidebarHandler={openAddReturnPanel}
                            />
                        </div>
                        {isLoading || !hasLatestCalculationStatus ? (
                            <div className={styles.loadingSpinnerContainer}>
                                <LoadingSpinner
                                    data-testid='loadingSpinner'
                                    id='loadingSpinner'
                                    label={t('loadingPleaseWait').toString()}
                                />
                            </div>
                        ) : (
                            <div className={styles.scrollablePane}>
                                <StyledTable
                                    created={onCreated}
                                    dataSource={dataManager}
                                    allowResizing
                                    allowSorting
                                    sortSettings={defaultSortingEnabled ? sortingOptions : undefined}
                                    pageSettings={pageSettings}
                                    enableVirtualization
                                    enableVirtualMaskRow
                                    height={contentLength === 0 || !hasMatch ? '42px' : '100%'}
                                    ref={gridRef}
                                    keyPressed={gridKeyPressed}
                                    dataBound={onDatabound}
                                    selectionSettings={selectionSettings}
                                    queryCellInfo={queryCellInfo}
                                    rowDataBound={handleRowDataBound}
                                    aria-busy={!isLoading}
                                    rowHeight={36}
                                    actionComplete={handleActionComplete}
                                    actionBegin={onActionBegin}
                                    searchSettings={searchSetting}
                                    key={tableKey}
                                    rowSelected={handleRowSelected}
                                    rowDeselected={handleRowSelected}
                                    loadingIndicator={{ indicatorType: 'Shimmer' }}
                                    editSettings={{ allowDeleting: true }}
                                >
                                    <ColumnsDirective>
                                        <ColumnDirective field='id' isPrimaryKey={true} visible={false} />
                                        <ColumnDirective
                                            type='checkbox'
                                            template={getWarningIconElement}
                                            width='40'
                                            allowResizing={false}
                                        />
                                        <ColumnDirective
                                            field={name}
                                            customAttributes={{ class: REPORT_NAME_CELL_FOCUS_CLASS }}
                                            headerText={t('report').toString()}
                                            headerTemplate={reportHeaderTemplate}
                                            template={reportNameTemplateTruncated}
                                            width='45%'
                                            minWidth='45%'
                                            headerTextAlign='Center'
                                            sortComparer={sortComparer}
                                        />
                                        <ColumnDirective
                                            field='status'
                                            headerText={t('status').toString()}
                                            headerTemplate={reportHeaderTemplate}
                                            template={reportStatusTemplate}
                                            width='170px'
                                            headerTextAlign='Center'
                                            sortComparer={sortComparer}
                                            allowSearching={false}
                                        />
                                        <ColumnDirective
                                            field={entityFilterName}
                                            headerText={t('entity').toString()}
                                            headerTemplate={reportHeaderTemplate}
                                            template={getColumnTemplate(entityFilterName)}
                                            width='10%'
                                            minWidth='10%'
                                            headerTextAlign='Center'
                                            sortComparer={sortComparer}
                                        />
                                        <ColumnDirective
                                            field={jurisdictionFilterName}
                                            headerText={t('jurisdiction').toString()}
                                            headerTemplate={reportHeaderTemplate}
                                            template={getColumnTemplate(jurisdictionFilterName)}
                                            width='7%'
                                            minWidth='7%'
                                            headerTextAlign='Center'
                                            sortComparer={sortComparer}
                                        />
                                        <ColumnDirective
                                            field={periodFilterName}
                                            headerText={t('period').toString()}
                                            headerTemplate={reportHeaderTemplate}
                                            template={getColumnTemplate(periodFilterName)}
                                            width='7%'
                                            minWidth='7%'
                                            headerTextAlign='Center'
                                            sortComparer={sortComparer}
                                        />
                                        <ColumnDirective
                                            field={caseFilterName}
                                            headerText={t('case').toString()}
                                            headerTemplate={reportHeaderTemplate}
                                            template={getColumnTemplate(caseFilterName)}
                                            width='5%'
                                            minWidth='5%'
                                            headerTextAlign='Center'
                                            sortComparer={sortComparer}
                                        />
                                        <ColumnDirective
                                            field='executed'
                                            headerText={t('executed').toString()}
                                            headerTemplate={reportHeaderTemplate}
                                            template={getExecutedTemplate}
                                            width='12%'
                                            minWidth='12%'
                                            headerTextAlign='Center'
                                            sortComparer={sortComparer}
                                            allowSearching={false}
                                        />
                                        <ColumnDirective
                                            field={userFullName}
                                            headerText={t('executedBy').toString()}
                                            headerTemplate={reportHeaderTemplate}
                                            template={getColumnTemplate(userFullName)}
                                            width='12%'
                                            minWidth='12%'
                                            headerTextAlign='Center'
                                            sortComparer={sortComparer}
                                        />
                                    </ColumnsDirective>
                                    <Inject services={[Sort, VirtualScroll, Resize, ContextMenu, Edit]} />
                                </StyledTable>
                                <AddReturnPanel
                                    isPanelOpen={isAddReturnPanelOpen}
                                    handleClose={closeAddReturnPanel}
                                    onSuccess={closeAddReturnPanelAndRefresh}
                                    allReturnsNames={allReturnsNames ?? []}
                                />
                            </div>
                        )}
                    </div>
                    <EditReturnModal
                        isOpen={isEditReturnModalOpen}
                        selectedReturn={selectedReturn}
                        onClose={closeEditReturnModal}
                        onSubmit={onSubmitEditReturn}
                        onDismissed={() => focusOnSelectedCell(false)}
                        allReturnsNames={allReturnsNames ?? []}
                        hasEditTaxReturnsDataEnabled={functionalAccess?.editTaxReturnsDataEnabled}
                    />
                    <ConfirmReturnDeletionModal
                        isModalVisible={isDeleteReturnModalOpen}
                        onConfirm={(returnKey, response) => {
                            closeDeleteReturnModal();
                            if (!response.hasErrors) {
                                displayFeedbackMessage({ type: 'success', message: t('calcDeleted') });
                                deleteTaxReturnRowDataInPlace(returnKey);
                                queryClient.invalidateQueries(getTaxReturnsNameListQueryKey);
                            }
                            if (response.hasErrors) {
                                displayFeedbackMessage({ type: 'error', message: t('calcNotDeleted') });
                            }
                        }}
                        onCancel={closeDeleteReturnModal}
                        returnKey={returnToDelete}
                    />
                </TaxReturnCalculationProvider>
            </ReturnDetailsContextProvider>
        </GridStateContextProvider>
    );
};

export const handleDisableActionButtons = (gridRef: RefObject<GridComponent>, setDisableActionButtons: (value: boolean) => void) => {
    const selectedRecords = gridRef.current?.getSelectedRecords();
    setDisableActionButtons(!selectedRecords || selectedRecords.length === 0);
};

export default withAITracking(applicationInsightsReactPlugin, ReportsTable, 'List of returns');
